import React, { useEffect, useState } from "react";
import AdminLayout from "../../Layout/index.jsx";
import {
  useGetCampusQuery,
  useGetClusterQuery, 
  useGetClustersByCampusQuery,
  useGetLeaderboardQuery,
  useGetStaffByEmailIdQuery,
  useGetStudentsByCampusQuery,
} from "../../../../graphql/generated.tsx";
import LeaderboardStaff from "../../Components/LeaderBoardStaff.jsx";
import {
  Spinner,
  Table,
  Tabs,
  TextInput,
  Button,
  Pagination,
  Select,
} from "flowbite-react";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { MdDashboard, MdOutlinePersonOff } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { TiGroup } from "react-icons/ti";
import { FaPerson } from "react-icons/fa6";
import formatDate from "../../Helpers/formatDate.jsx";
import { AiOutlineDownload } from "react-icons/ai";
import StudentLookupTab from "../Dashboards/StudentLookupTab.jsx";
import BatchAnalysisTab from "../Dashboards/BatchAnalysisTab.jsx";
import Loader from "../../../../components/Loader.jsx";

const StaffDashboard = () => {
  const navigate = useNavigate();
  const email = JSON.parse(localStorage.getItem("user"))?.email;
  const { data: staffData } = useGetStaffByEmailIdQuery({
    variables: { email },
  });

   const [Isload, setIsload] = useState(true);

   useEffect(()=>{
    setTimeout(()=>{
      setIsload(false);
    },2000);
  },[]);

  const { data: campusDetails, loading: campusLoading } = useGetCampusQuery({
    variables: { id: staffData?.getStaffByEmailId?.campusId },
    skip: !staffData,
  });
  const campus = campusDetails?.campus;

  const { data: clustersData, loading: clustersLoading } =
    useGetClustersByCampusQuery({
      variables: { campusName: campus?.name },
      skip: !campus,
      limit: 200,
    });

  const campusName = campus?.name;

  const handleFilter = (value) => {
    setFilter(value);
  };

  const availableClusters = staffData?.getStaffByEmailId?.isAdminStaff
    ? clustersData?.getClustersByCampus
    : clustersData?.getClustersByCampus.filter((cluster) =>
        staffData?.getStaffByEmailId?.clusterIds?.includes(cluster.id)
      );

  const [selectedCluster, setSelectedCluster] = useState(
    availableClusters?.[0]?.id
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [searchClicked, setSearchClicked] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("custom");
  const [fetchLeaderboard, setFetchLeaderboard] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // Fix: define activeTab and setActiveTab in component state
  const studentsPerPage = 20;
  const clusterId = selectedCluster || availableClusters?.[0]?.id;

  const { data } = useGetClusterQuery({
    variables: { clusterId: clusterId, campusName: campusName },
  });

  const {
    data: studentData,
    loading: studentLoading,
    refetch,
  } = useGetStudentsByCampusQuery({
    variables: {
      campusId: campus?.id,
      clusterid: selectedCluster || availableClusters?.[0]?.id,
      searchTerm: searchClicked ? searchTerm : "",
      page,
      limit: studentsPerPage,
    },
  });

  const handleSearchClick = () => {
    setSearchClicked(true);
    refetch({
      campusId: campus?.id,
      clusterid: selectedCluster,
      searchTerm,
    });
    setSearchClicked(false);
  };

  const totalStudents = studentData?.getStudentsByCampus?.total || 0;
  const totalPages = Math.ceil(totalStudents / studentsPerPage);

  const {
    data: leaderboardData,
    loading: leaderboardLoading,
    error: leaderboardError,
    refetch: leaderboardRefetch,
  } = useGetLeaderboardQuery({
    variables: {
      campusName: campus?.name,
      clusterId: selectedCluster || availableClusters?.[0]?.id,
      page: 1,
      limit: 20,
    },
    skip: !fetchLeaderboard,
  });

  // leaderboard calculations (move above downloadExcel)
  const leaderboard = leaderboardData?.getLeaderboard || [];
  const notStartedStudents = leaderboard.filter((student) => student.totalScore === 0);
  const filteredLeaderboard = leaderboard.filter((student) => student.totalScore > 0);
  const top20Students = filteredLeaderboard.slice(0, 20);
  const least20Students = filteredLeaderboard.length > 20 ? filteredLeaderboard.slice(-20) : [];

  const downloadExcel = () => {
    const headers = [
      "Position",
      "Student Email",
      "Last Submission Date",
      "Total Score",
      "Total Completed Questions",
      "Easy Questions Completed (Course)",
      "Medium Questions Completed (Course)",
      "Hard Questions Completed (Course)",
      "Total Leetcode Questions Completed",
      "Leetcode Easy",
      "Leetcode Medium",
      "Leetcode Hard",
    ];
    const getData = (students) =>
      students.map((student, index) => {
        const leetcodeEasy =
          student.leetcode_data?.find((data) => data.difficulty === "Easy")
            ?.count || 0;
        const leetcodeMedium =
          student.leetcode_data?.find((data) => data.difficulty === "Medium")
            ?.count || 0;
        const leetcodeHard =
          student.leetcode_data?.find((data) => data.difficulty === "Hard")
            ?.count || 0;

        return [
          index + 1,
          student.student.email,
          student.latestSubmissionDate
            ? formatDate(student.latestSubmissionDate)
            : "Not yet started",
          student.totalScore,
          student.student.completedQuestions?.length || 0,
          student.easyQuestionsCompleted || 0,
          student.mediumQuestionsCompleted || 0,
          student.hardQuestionCompleted || 0,
          leetcodeEasy + leetcodeMedium + leetcodeHard,
          leetcodeEasy,
          leetcodeMedium,
          leetcodeHard,
        ];
      });

    const workbook = XLSX.utils.book_new();
    const addSheet = (data, sheetName) => {
      const sheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
      XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    };

    addSheet(getData(top20Students), "Top 20 Students");
    addSheet(getData(least20Students), "Least 20 Students");
    addSheet(getData(notStartedStudents), "Not Yet Started");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      `Leaderboard_${campusName}_${data?.getCluster?.name}.xlsx`
    );
  };

  if (clustersLoading || studentLoading || Isload) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="overflow-x-auto">
        <Tabs
          aria-label="Full width tabs"
          variant="fullWidth"
          activeIndex={activeTab}
          onActiveTabChange={setActiveTab}
        >
          <Tabs.Item title="Student Lookup" icon={FaPerson}>
            <StudentLookupTab
              availableClusters={availableClusters}
              selectedCluster={selectedCluster}
              setSelectedCluster={setSelectedCluster}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              handleSearchClick={handleSearchClick}
              studentData={studentData}
              studentLoading={studentLoading}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              navigate={navigate}
            />
          </Tabs.Item>
          <Tabs.Item title="Batch Analysis" icon={TiGroup}>
            <BatchAnalysisTab
              availableClusters={availableClusters}
              selectedCluster={selectedCluster}
              setSelectedCluster={setSelectedCluster}
              leaderboardData={leaderboardData}
              leaderboardLoading={leaderboardLoading}
              leaderboardError={leaderboardError}
              leaderboardRefetch={leaderboardRefetch}
              filter={filter}
              setFilter={setFilter}
              downloadExcel={downloadExcel}
              campusName={campusName}
              clusterId={clusterId}
            />
          </Tabs.Item>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default StaffDashboard;